import gql from 'graphql-tag';
import { useMutation } from '@apollo/react-hooks';
import { useQuery } from "@apollo/client";
import { Box, CircularProgress, makeStyles, Paper, TextField, Typography } from '@material-ui/core';
import ProjectSchema from './projectSchema';
import Button from '@material-ui/core/Button';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import React, { useReducer, useState } from 'react';
import { Autocomplete } from '@material-ui/lab';

// noinspection GraphQLUnresolvedReference
const GET_PROJECT_SCHEMA = gql`
    query ProjectSchema($projectId: Int!) {
        Project(id: $projectId) {
            id
            name
        }
        allRecordTypes(filter: {project_id: $projectId}) {
            id
            name
            category
            sub_category
            order
            descriptions {
                id
                name
                type
                order
                listValues {
                    id
                    value
                    order
                }
            }
        }
    }
`;

// noinspection GraphQLUnresolvedReference
const GET_ALL_PROJECTS = gql`
    query {
        allProjects(sortField:"name", perPage:10000){
            id,
            name
        }
    }
`;

// noinspection GraphQLUnresolvedReference
const COPY_RT_SCHEMA_TO_PROJECT = gql`
    mutation copyRTSchemaToProject($projectId: Int!, $rtSchema: CreateRecordTypeInput!,$permissions: NewRecordTypePermissions!) {
        createRecordType(
            project_id: $projectId,
            createRecordTypeInput: $rtSchema,
            permissions:$permissions
        ){
            id
        }
    }
`;


const useStyles = makeStyles(theme => ({
  page: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    // backgroundColor: 'red',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  panel: {
    height: '100%',
    width: '40%',
    flexGrow: 1,
  },
  panelTitle: {
    ...theme.typography.h6,
    fontWeight: 'bold',
    paddingBottom: theme.spacing(1),
  },
  schemaContainer: {
    width: '100%',
  },
  projectHeaderBox: {
    padding: theme.spacing(3.5),
    paddingLeft: theme.spacing(2),
  },
  projectSelectorBox: {
    padding: theme.spacing(2),
  },
  progressBtnWrapper: {
    margin: theme.spacing(1),
    position: 'relative',
  },
  copyBtn: {
    margin: theme.spacing(4),
  },
  buttonProgressSpinner: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}));

const initialSchemaEditState = {
  selections: {},
};
const schemaEditReducer = (state, action) => {
  switch (action.type) {
    case 'SELECT_RECORD_TYPE': {
      const selections = { ...state.selections, [action.rt.id]: action.rt };
      return { ...state, selections };
    }
    case 'DESELECT_RECORD_TYPE': {
      const { [action.rt.id]: _, ...remaining } = state.selections;
      return { ...state, selections: remaining }
    }
    case 'CLEAR_SELECTIONS':
      return { ...state, selections: {} };
    default:
      return state;
  }
};

const ProjectSchemaEditPage = ({ projectId }) => {
  const classes = useStyles();

  const [copying, setCopying] = useState(false);

  const [projectSchemaEditState, projectSchemaEditDispatch] = useReducer(schemaEditReducer, initialSchemaEditState);
  const [templateSchemaEditState, templateSchemaEditDispatch] = useReducer(schemaEditReducer, initialSchemaEditState);

  const { data: getAllProjectsData } = useQuery(GET_ALL_PROJECTS);

  const { data: getProjectSchemaData, refetch: refetchProjectSchema } = useQuery(GET_PROJECT_SCHEMA, {
    variables: { projectId: projectId && parseInt(projectId) },
  });

  const [selectedTemplateProject, setSelectedTemplateProject] = useState();

  const { data: getTemplateSchemaData } = useQuery(GET_PROJECT_SCHEMA, {
    variables: { projectId: selectedTemplateProject && parseInt(selectedTemplateProject.id) },
    skip: selectedTemplateProject == null,
  });

  const [ copySchema ] = useMutation(COPY_RT_SCHEMA_TO_PROJECT);

  const ProjectSelector = ({templateProjects}) => (
    <Autocomplete
      value={selectedTemplateProject}
      onChange={(event, newValue) => {
        setSelectedTemplateProject(newValue);
        templateSchemaEditDispatch({ type: 'CLEAR_SELECTIONS'});
      }}
      options={templateProjects}
      getOptionLabel={(option) => option.name}
      renderInput={(params) =>
        <TextField {...params} label="Template project" variant="outlined" placeholder="Select template project..."
                   InputLabelProps={{ shrink: true }}
        />
      }
    />
  );

  const removeIDs = (rt) => {
    const { id, __typename, ...rtRest } = rt;
    if (rt.descriptions && rt.descriptions.length) {
      const strippedDesc = rt.descriptions.map((d) => {
        const { id, __typename, ...descRest } = d;
        if (d.listValues && d.listValues.length) {
          const strippedLVs = d.listValues.map((lv) => {
            const { id, __typename, ...lvRest } = lv;
            return lvRest;
          });
          return { ...descRest, listValues: strippedLVs }
        }
        return descRest;
      });
      return { ...rtRest, descriptions: strippedDesc }
    }
    return rtRest;
  };

  const prepSchemaForCopy = (rt) => {
    let prepped;
    prepped = removeIDs(rt);
    // TODO: prevent duplicate record type names
    return prepped;
  };

  const handleCopySchema = async () => {
    setCopying(true);
    try {
      await Promise.all(Object.values(templateSchemaEditState.selections).map((rt) => {
        const preppedSchema = prepSchemaForCopy(rt);
        return copySchema({
          variables: {
            projectId: projectId && parseInt(projectId),
            rtSchema: preppedSchema,
            permissions:'FULL'
          }
        });
      }));
      await refetchProjectSchema();
      templateSchemaEditDispatch({ type: 'CLEAR_SELECTIONS' });
    } finally {
      setCopying(false);
    }
  };

  const { selections: templateSelections } = templateSchemaEditState;
  return (
    <Box className={classes.page}>
      <Box className={classes.panel}>
        <Typography className={classes.panelTitle} variant="h6" gutterBottom>Project Schema</Typography>
        <Paper className={classes.schemaContainer} variant="outlined">
          {getProjectSchemaData &&
          <Box className={classes.projectHeaderBox}>
            <Typography variant="h6">{getProjectSchemaData.Project.name}</Typography>
          </Box>
          }
          <ProjectSchema schema={getProjectSchemaData} schemaEditState={projectSchemaEditState} schemaEditDispatch={projectSchemaEditDispatch}/>
        </Paper>
      </Box>

      <Box className={classes.progressBtnWrapper}>
        <Button className={classes.copyBtn} variant='contained'
                color='primary' size='large'
                disabled={Object.keys(templateSelections).length === 0 || copying}
                onClick={handleCopySchema}
        >
          <ChevronLeft/> Copy
        </Button>
        {copying && <CircularProgress size={24} className={classes.buttonProgressSpinner}/>}
      </Box>

      <Box className={classes.panel}>
        <Typography className={classes.panelTitle} variant="h6" gutterBottom>Templates</Typography>
        <Paper className={classes.schemaContainer} variant="outlined">
          {getAllProjectsData &&
          <Box className={classes.projectSelectorBox}>
            <ProjectSelector templateProjects={getAllProjectsData.allProjects}/>
          </Box>
          }
          <ProjectSchema schema={getTemplateSchemaData} schemaEditState={templateSchemaEditState} schemaEditDispatch={templateSchemaEditDispatch} selectableRT/>
        </Paper>
      </Box>
    </Box>
  )
};

export default ProjectSchemaEditPage;
